import { action, configure, makeObservable, observable } from "mobx";
import { transformExtent } from "ol/proj";
import ChartLayer, { ChartLayerOptions } from "../layers/ChartLayer";
import { OsmVectorLayerOptions } from "../layers/ChartSubLayers/OsmVectorLayer";
import GraticuleLayer, { GraticuleLayerOptions } from "../layers/GraticuleLayer";
import { IChartConfig } from "../models/ChartModels";
import { IColorScale } from "../models/ColorScale";
import { IExternalLayerSettings } from "../models/IExternalLayerSettings";
import { Projections } from "../models/Projections";
import { getExtent } from "../View";
import { ChartStore } from "./ChartStore";
import { ChartWmsStore } from "./ChartWmsStore";
import { ExternalLayersStore } from "./ExternalLayersStore";
import { GraticuleStore } from "./GraticuleStore";
import { MapControlsStore } from "./MapControlsStore";
import { LocalStorageKey, LocalStorageStores, MapStoreLocalStorage } from "./MapStoreLocalStorage";
import { PickReportStore } from "./PickReportStore";
import { TokenInfo, TokenStore } from "./TokenStore";
import { UnitsStore } from "./UnitsStore";

configure({ enforceActions: "observed" });

export type MapStoreAutoSave = { [key in LocalStorageKey]?: boolean } | boolean;

/**
 * Contains collection of stores and layers essential for creating a map
 * */
export class MapStore implements Partial<LocalStorageStores> {

    /** @deprecated useChartLayer hook instead */
    public chartLayer: ChartLayer | undefined = undefined;

    /** @deprecated useGraticuleLayer hook instead */
    public graticuleLayer: GraticuleLayer | undefined = undefined;

    public chart: ChartStore;
    public chartWms: ChartWmsStore;
    public token: TokenStore;
    public graticule: GraticuleStore;
    public mapControls: MapControlsStore;
    public units: UnitsStore;
    public external?: ExternalLayersStore;
    public pickReport: PickReportStore;   

    /** 
     * Whether autosaving to local storage is enabled for all for some stores. 
     * Disabled by default.
     * */   
    public autoSaveEnabled: MapStoreAutoSave = false;

    //#region Private

    private localStorage: { [K in LocalStorageKey]?: MapStoreLocalStorage<K, LocalStorageStores[K]> };
    private canAutoSave = (store: LocalStorageKey) => {
        if (typeof this.autoSaveEnabled === 'boolean')
            return this.autoSaveEnabled;
        else
            return this.autoSaveEnabled[store] ?? false;
    }

    //#endregion

    constructor(getToken?: () => Promise<TokenInfo>, externalLayersSettings?: IExternalLayerSettings, getColorScalesFn?: (additionalLookupKeys?: number[]) => Promise<IColorScale[]>) {
        makeObservable(this, {
            chartLayer: observable.ref,
            graticuleLayer: observable.ref,
            autoSaveEnabled: observable,
            createChartLayer: action.bound,            
            createGraticuleLayer: action.bound,
            disposeChartLayer: action.bound,
            disposeGraticuleLayer: action.bound,            
            enableAutoSave: action.bound
        });

        this.token = new TokenStore(getToken);
        this.chart = new ChartStore(getColorScalesFn);   
        this.chartWms = new ChartWmsStore();
        this.graticule = new GraticuleStore();
        this.mapControls = new MapControlsStore();
        this.units = new UnitsStore();

        this.localStorage = {
            chart: new MapStoreLocalStorage('chart', this.chart, this.canAutoSave, state => {
                if (state.bathymetry?.colorScales) {
                    const bathy = { ...state.bathymetry };
                    delete bathy.colorScales;
                    state.bathymetry = bathy;                   
                }

                return state;
            }),
            units: new MapStoreLocalStorage('units', this.units, this.canAutoSave),
            graticule: new MapStoreLocalStorage('graticule', this.graticule, this.canAutoSave),
            mapControls: new MapStoreLocalStorage('mapControls', this.mapControls, this.canAutoSave)
        }

        if (externalLayersSettings) {
            this.external = new ExternalLayersStore(externalLayersSettings);
            this.localStorage.external = new MapStoreLocalStorage('external', this.external, this.canAutoSave, ({ selectedLayerId }) => ({ selectedLayerId }));
        }        

        this.pickReport = new PickReportStore();
    }

    /** @deprecated useChartLayer hook instead */
    public async createChartLayer(
        config: IChartConfig,
        chartLayerOptions?: ChartLayerOptions,
        osmVectorLayerOptions?: OsmVectorLayerOptions,
        externalLayerOptions?: ChartLayerOptions
    ) {

        this.chartLayer = new ChartLayer(config, {
            chart: this.chart,
            token: this.token,
            external: this.external
        }, {
            extent: transformExtent(getExtent(), Projections.WGS, Projections.WebMercator),
            ...chartLayerOptions
        }, osmVectorLayerOptions, undefined, externalLayerOptions);   
        
        await this.token.load();        

        return this.chartLayer;
    }

    /** @deprecated useChartLayer hook instead */
    public disposeChartLayer() {
        this.chartLayer?.dispose();
        this.chartLayer = undefined;
    }

    /** @deprecated useGraticuleLayer hook instead */
    public createGraticuleLayer(options?: GraticuleLayerOptions) {
        this.graticuleLayer = new GraticuleLayer({
            graticule: this.graticule,
            units: this.units
        }, options);
        return this.graticuleLayer;
    }   

    /** @deprecated useGraticuleLayer hook instead */
    public disposeGraticuleLayer() {
        this.graticuleLayer?.dispose();
        this.graticuleLayer = undefined;
    }   

    /**
     * Enables autosaving to local storage.
     * Disabled by default.
     * @param value enable autosave for all or some stores.
     */
    public enableAutoSave(value: MapStoreAutoSave) {
        this.autoSaveEnabled = value;
    }

    /**
     * Load states from local storage.
     * Note: Must be triggered manually.
     * @param stores Stores to load, use undefined to load all
     */
    public loadFromLocalStorage(stores?: LocalStorageKey[]) {
        let key: LocalStorageKey;
        for (key in this.localStorage) {
            if (!stores || stores.includes(key)) {
                this.localStorage[key]?.load();
            }
        }    
    }

    /**
     * Save states to local storage.
     * Note: Saving is triggered automatically when the state of a store is updated while autosave is enabled. But it can be manually triggered here.
     * @param stores Stores to save, use undefined to save all
     */
    public saveToLocalStorage(stores?: LocalStorageKey[]) {
        let key: LocalStorageKey;
        for (key in this.localStorage) {
            if (!stores || stores.includes(key)) {
                this.localStorage[key]?.save();
            }
        }
    }      
}